import React from 'react';
import { Container } from 'reactstrap';
import Layout from '../../templates/Layout';
import { useMutation, useQuery } from '@apollo/client';
import {
  CREATE_PROCEDURE,
  GET_ALL_PROCEDURES,
  LINK_PROCEDURE_JOBS,
  REMOVE_PROCEDURE,
  UPDATE_PROCEDURE,
} from '../../../graph-queries/tar/procedure';
import { TProcedure } from '../../../lib/types';
import { useSelectedAsset } from '../../../lib/util';
import { useHasTarAdminAccess, useHasUserAccess } from '../../../lib/util/auth';
import AspectsTable from '../../organisms/AspectsTable/AspectsTable';
import ProcedureForm from '../../organisms/ProcedureForm/ProcedureForm';
import { ColumnDef } from '@tanstack/react-table';
import { authorCell, useAlertText } from '../../organisms/AspectsTable/util';
import { PROCEDURE_CATEGORY_ENUM, PROCEDURE_STATUS_ENUM } from '../../../lib/constants/dbConstants';

export const columns: ColumnDef<TProcedure>[] = [
  {
    header: 'Title',
    accessorKey: 'title',
  },
  {
    header: 'Procedure Number',
    accessorKey: 'procedureNumber',
  },
  {
    header: 'Category',
    accessorKey: 'category',
    accessorFn: (row) => PROCEDURE_CATEGORY_ENUM[row.category],
  },
  {
    header: 'URL',
    accessorKey: 'url',
    enableSorting: false,
    cell: ({ row }) => (
      <a href={row.original.url} target="_blank" rel="noopener noreferrer">
        {row.original.url}
      </a>
    ),
  },
  {
    header: 'Status',
    accessorKey: 'status',
    accessorFn: (row) => PROCEDURE_STATUS_ENUM[row.status],
  },
  {
    header: 'Comments',
    accessorKey: 'comments',
    enableSorting: false,
  },
  {
    header: 'Last Updated',
    accessorKey: 'updatedAt',
    cell: authorCell,
  },
];

const ProceduresPage: React.FC = () => {
  const { alertText, setAlertText } = useAlertText();
  const hasUserAccess = useHasUserAccess();
  const hasTarAdminAccess = useHasTarAdminAccess();
  const asset = useSelectedAsset();
  const { data, loading } = useQuery(GET_ALL_PROCEDURES, {
    variables: { assetId: asset?.assetId || -1 },
    skip: !asset?.assetId,
  });
  const [createProcedure, { loading: isCreating }] = useMutation(CREATE_PROCEDURE, {
    refetchQueries: [GET_ALL_PROCEDURES],
    awaitRefetchQueries: true,
  });
  const [updateProcedure, { loading: isUpdating }] = useMutation(UPDATE_PROCEDURE, {
    refetchQueries: [GET_ALL_PROCEDURES],
    awaitRefetchQueries: true,
  });
  const [linkProcedureJobs, { loading: isLinking }] = useMutation(LINK_PROCEDURE_JOBS, {
    refetchQueries: [GET_ALL_PROCEDURES],
    awaitRefetchQueries: true,
  });
  const [removeProcedure, { loading: isRemoving }] = useMutation(REMOVE_PROCEDURE, {
    refetchQueries: [GET_ALL_PROCEDURES],
    awaitRefetchQueries: true,
  });

  const onAddSubmit = (procedure: TProcedure) => {
    const { procedureId, updatedBy, updatedAt, tarJobs, ...input } = procedure;
    return createProcedure({ variables: { input } }).then(() =>
      setAlertText('Procedure successfully added')
    );
  };

  const onEditSubmit = (procedure: TProcedure) => {
    const { updatedBy, updatedAt, tarJobs, ...input } = procedure;
    return updateProcedure({ variables: { input } }).then(() =>
      setAlertText('Procedure successfully updated')
    );
  };

  const onLinkSubmit = (selection: TProcedure[], jobIds: number[]) => {
    return Promise.all(
      selection.map((procedure) =>
        linkProcedureJobs({
          variables: {
            procedureId: procedure.procedureId,
            jobIds: jobIds,
          },
        })
      )
    ).then(() =>
      setAlertText(
        `Procedure successfully linked to ${jobIds.length} job${jobIds.length > 1 ? 's' : ''}`
      )
    );
  };

  const onRemoveSubmit = (selection: TProcedure[]) => {
    return Promise.all(
      selection.map((procedure) =>
        removeProcedure({
          variables: { procedureId: procedure.procedureId },
        })
      )
    );
  };

  const isLoading = isCreating || isUpdating || isLinking || isRemoving || loading || !!alertText;
  return (
    <Layout>
      <Container>
        {loading ? (
          'Loading...'
        ) : (
          <AspectsTable<TProcedure>
            hideSelectionColumn
            aspectName="procedure"
            data={(data?.allProcedures as unknown as TProcedure[]) || []}
            onLinkSubmit={onLinkSubmit}
            onRemoveSubmit={onRemoveSubmit}
            columns={columns}
            alertText={alertText}
            renderAddForm={(onClose) => (
              <ProcedureForm
                onSubmit={onAddSubmit}
                onClose={onClose}
                type={'add'}
                isLoading={isLoading}
              />
            )}
            renderEditForm={(onClose, defaultValue) => (
              <ProcedureForm
                defaultValues={defaultValue}
                onSubmit={onEditSubmit}
                onClose={onClose}
                type={'edit'}
                isLoading={isLoading}
              />
            )}
            creationDisabled={!hasUserAccess}
            getRowMutationDisabled={(_row) => !hasUserAccess}
            getRowDeletionDisabled={(_row) => !hasTarAdminAccess}
            searchEnabled={true}
            searchPlaceholderText="Search the procedure tracker..."
            isLoading={isLoading}
          />
        )}
      </Container>
    </Layout>
  );
};

export default ProceduresPage;
